'use client';

import { getClientApplicationConfig } from '@hc-frontend/core-context';
import type { TextAreaProps } from '@hc-frontend/core-ui';
import { ErrorMessage, Input, Select, TextArea } from '@hc-frontend/core-ui';
import type { DncResponse } from '@hc-frontend/services';
import { ButtonWithTracking } from '@hc-frontend/shells-shared';
import type React from 'react';
import { useActionState, useEffect, useState } from 'react';

import getRecaptchaToken from '../../../utils/get-recaptcha-token';
import Recaptcha from '../../atoms/recaptcha/recaptcha';
import type { FormState } from './constants';
import Container from './container';
import { actionForm } from './form-action-server';

function Submit({ isPending }: { isPending: boolean }) {
  return (
    <div className="hc-center-layout hc-stack-layout my-16-16">
      <div className="hc-center-layout my-16-16 flex flex-col flex-wrap justify-center gap-12-12">
        <ButtonWithTracking
          id="submit-button"
          variant="primary-card"
          type="submit"
          className="w-full justify-center px-32-60 text-center tablet:w-fit"
          disabled={isPending}
          metricTags={{}}
          name="submit"
          value="submit"
        >
          Submit
        </ButtonWithTracking>
      </div>
    </div>
  );
}

export function Unsubscribe({
  isAdmin,
  email,
  setDncResponse,
}: {
  isAdmin: boolean;
  email: string | null;
  setDncResponse: React.Dispatch<React.SetStateAction<DncResponse | undefined>>;
}) {
  const config = getClientApplicationConfig();
  const [requestDetails, setRequestDetails] = useState('');

  const handleRequestDetailsChange: TextAreaProps['onChange'] = (e) => {
    setRequestDetails(e.target.value);
  };

  const selectOptions = [
    {
      id: 'User Generated',
      label: 'User Generated',
    },
    {
      id: 'Calm DNC request',
      label: 'Calm DNC request',
    },
    {
      id: 'Overdialling',
      label: 'Overdialling',
    },
    {
      id: 'Extreme Overdialling - Threatening Legal Action',
      label: 'Extreme Overdialling - Threatening Legal Action',
    },
    {
      id: 'Extreme Overdialling - Already Taken Legal Action',
      label: 'Extreme Overdialling - Already Taken Legal Action',
    },
  ];

  const securedActionForm = async (_: FormState, formData: FormData) => {
    const token = await getRecaptchaToken(
      config.recaptcha.siteKey,
      'unsubscribe_request',
    );
    formData.append('grecaptcha_token', token);
    return actionForm(_, formData);
  };

  const [state, formSubmitHandler, isPending] = useActionState(
    securedActionForm,
    {
      _errors: [],
      dncResponse: undefined,
    },
  );

  useEffect(() => {
    if (state.dncResponse) {
      setDncResponse(state.dncResponse);
    }
  }, [state.dncResponse, setDncResponse]);

  return (
    <div>
      <Recaptcha />
      <h1 className="hc-typography--heading-three tablet:hc-typography--heading-two tablet:hc-typography--heading-one my-2-12 text-center">
        Do not contact request form
      </h1>
      <p className="hc-typography--paragraph-md my-2-12 text-center">
        Please submit your request below
      </p>
      <form action={formSubmitHandler} className="w-full">
        <Input
          name="email"
          id="email"
          label="Your Email Address"
          placeholder="Your Email Address"
          required
          readOnly
          type="email"
          value={email || ''}
        />
        <ErrorMessage>{state.email?._errors}</ErrorMessage>
        <input name="isAdmin" type="hidden" value={isAdmin.toString()} />
        {isAdmin && (
          <div className="hc-stack-layout--small py-2-40">
            <div className="text-center">
              <ErrorMessage>{state._errors}</ErrorMessage>
            </div>
            <div className="hc-grid-layout grid grid-cols-[1fr] tablet:grid-cols-[2fr_2fr] ">
              <Container
                name="email_check"
                headline="Email (all)"
                content="I don’t want any emails (marketing, important healthcare reminders, weekly tips, and quarterly newsletters)"
                tooltipText="We send you emails with quotes you requested, information on how to maximize your healthcare, important new, and important deadlines related to healthcare."
                tooltipClassName="left-[-150px] top-[-105px] w-[330px] bg-primary-background-50 text-text-950"
              />
              <Container
                name="phone_check"
                headline="Phone Calls"
                content="I don’t want any phone calls."
                tooltipText="Licensed agents from our partner companies may call you to assist you in your journey to find healthcare."
                tooltipClassName="left-[-150px] top-[-85px] w-[330px] bg-primary-background-50 text-text-950"
              />
              <Container
                name="sms_check"
                headline="SMS Messages"
                content="I don’t want any text messages"
                tooltipText="Licensed agents from our partner companies may send you SMS text messages regarding your journey to find healthcare. We may also contact you via SMS message to check that you’ve received the service you requested."
                tooltipClassName="left-[-150px] top-[-125px] w-[330px] bg-primary-background-50 text-text-950"
              />
              <Container
                name="push_check"
                headline="Push Notifications"
                content="I don’t want any browser notifications"
                tooltipText="We will periodically send reminders for important deadlines, important healthcare news, or announcements to your internet browser."
                tooltipClassName="left-[-150px] top-[-95px] w-[330px] bg-primary-background-50 text-text-950"
              />
            </div>
          </div>
        )}
        <TextArea
          name="request_details"
          label="Request Details"
          onChange={handleRequestDetailsChange}
          value={requestDetails}
        />

        {isAdmin && (
          <Select name="severity" label="Severity" options={selectOptions} />
        )}

        <Submit isPending={isPending} />
      </form>
    </div>
  );
}
